import { ENDPOINTS } from "@/common/service/config/endpoint";
import { axiosBaseQuery, queryHandler } from "@/common/service/request";
import { createApi } from "@reduxjs/toolkit/query/react";
import {
  APIResponseModel,
  IappWallet,
  IbankWithdrawal,
  IbankWithdrawalResponse,
  IWalletType,
  IwithdrawalTransactionThunk,
  MerchantCardWalletType,
  UtilitiesWallet,
  WalletTransactionPayloadType,
} from "../types";
import { ItransactionItem } from "@/module/applications/types";
import customToast from "@/common/components/CustomToast";
import { IFundPolling } from "@/module/utility/types";

export const walletApi = createApi({
  reducerPath: "walletApi",
  baseQuery: axiosBaseQuery({ baseUrl: "" }),
  tagTypes: ["utilityWallet", "kycWallet", "treasuryWallet", "commisionWallet"],
  endpoints: (builders) => ({
    getTreasuryWallet: builders.query<APIResponseModel<IappWallet>, string>({
      query: (id) => {
        const { method, url: makeUrl } = ENDPOINTS.wallet.get_wallet;
        const url = makeUrl(id);

        return {
          method,
          url,
        };
      },
      providesTags: ["treasuryWallet"],
    }),
    getCommisionWallet: builders.query<APIResponseModel<IappWallet>, string>({
      query: (id) => {
        const { method, url: makeUrl } = ENDPOINTS.wallet.get_commision_wallet;
        const url = makeUrl(id);

        return {
          method,
          url,
        };
      },
      transformResponse: (
        response: APIResponseModel<APIResponseModel<IappWallet>>
      ) => response.data,
      providesTags: ["commisionWallet"],
    }),
    getCommisionLedgerBalance: builders.query<
      APIResponseModel<{ commission: number }>,
      string
    >({
      query: (id) => {
        const { method, url: makeUrl } = ENDPOINTS.wallet.get_ledger_balance;
        const url = makeUrl(id);

        return {
          method,
          url,
        };
      },
    }),
    getKycWallet: builders.query<APIResponseModel<IappWallet>, string>({
      query: (id) => {
        const { method, url: makeUrl } = ENDPOINTS.wallet.get_kyc_wallet;
        const url = makeUrl(id);

        return {
          method,
          url,
        };
      },
      transformResponse: (
        response: APIResponseModel<APIResponseModel<IappWallet>>
      ) => response.data,
      providesTags: ["kycWallet"],
    }),
    getTreasuryTransactions: builders.query<
      APIResponseModel<ItransactionItem[]>,
      IwithdrawalTransactionThunk
    >({
      query: (args) => {
        const { method, url: makeUrl } = ENDPOINTS.wallet.list_app_transactions;
        const { appid, ...otherQuery } = args;
        const query = queryHandler(otherQuery);
        const url = makeUrl(appid, query);

        return {
          method,
          url,
        };
      },
    }),
    getCommisionTransactions: builders.query<
      APIResponseModel<ItransactionItem[]>,
      IwithdrawalTransactionThunk
    >({
      query: (args) => {
        const { method, url: makeUrl } = ENDPOINTS.wallet.list_app_transactions;
        const { appid, ...otherQuery } = args;
        const query = queryHandler(otherQuery);
        const url = makeUrl(appid, query);

        return {
          method,
          url,
        };
      },
    }),
    getKycTransactions: builders.query<
      APIResponseModel<ItransactionItem[]>,
      IwithdrawalTransactionThunk
    >({
      query: (args) => {
        const { method, url: makeUrl } = ENDPOINTS.wallet.list_app_transactions;
        const { appid, ...otherQuery } = args;
        const query = queryHandler(otherQuery);
        const url = makeUrl(appid, query);

        return {
          method,
          url,
        };
      },
    }),
    getUtilitiesTransactions: builders.query<
      APIResponseModel<ItransactionItem[]>,
      IwithdrawalTransactionThunk
    >({
      query: (args) => {
        const { method, url: makeUrl } = ENDPOINTS.wallet.list_app_transactions;
        const { appid, ...otherQuery } = args;
        const query = queryHandler(otherQuery);
        const url = makeUrl(appid, query);

        return {
          method,
          url,
        };
      },
    }),
    getCardTransactions: builders.query<
      APIResponseModel<ItransactionItem[]>,
      IwithdrawalTransactionThunk
    >({
      query: (args) => {
        const { method, url: makeUrl } = ENDPOINTS.wallet.list_app_transactions;
        const { appid, ...otherQuery } = args;
        const query = queryHandler(otherQuery);
        const url = makeUrl(appid, query);

        return {
          method,
          url,
        };
      },
    }),
    getMerchantCardWallet: builders.query<
      APIResponseModel<MerchantCardWalletType>,
      string
    >({
      query: (appid: string) => {
        const { url: makeUrl, method } =
          ENDPOINTS.virtual_card.GET_MERCHANT_CARD_WALLET;
        const url = makeUrl(appid);
        return {
          url,
          method,
        };
      },
    }),
    getUtilitiesWallet: builders.query<
      APIResponseModel<UtilitiesWallet>,
      string
    >({
      query: (appid: string) => {
        const { method, url: makeUrl } = ENDPOINTS.utility.get_wallet;
        const url = makeUrl(appid);
        return {
          url,
          method,
        };
      },
      providesTags: ["utilityWallet"],
    }),
    fundUtilityWallet: builders.mutation({
      query: (data: WalletTransactionPayloadType) => {
        const { method, url: makeUrl } = ENDPOINTS.utility.fund_wallet;
        const url = makeUrl(data.appid);
        return {
          url,
          method,
          data: data.data,
        };
      },
      onQueryStarted: async (_, { queryFulfilled, dispatch }) => {
        try {
          const message = (await queryFulfilled).data.message;
          customToast.success(message);
          dispatch(walletApi.util.invalidateTags(["utilityWallet"]));
        } catch (error: any) {
          const message = error?.error?.data;

          if (typeof message === "string") customToast.error(message);
          else customToast.error("Unable to fund, try again later");
        }
      },
    }),
    withdrawUtilityWallet: builders.mutation({
      query: (data: WalletTransactionPayloadType) => {
        const { method, url: makeUrl } = ENDPOINTS.utility.withdraw_funds;
        const url = makeUrl(data.appid);
        return {
          url,
          method,
          data: data.data,
        };
      },
      onQueryStarted: async (_, { queryFulfilled, dispatch }) => {
        try {
          const message = (await queryFulfilled).data.message;
          customToast.success(message);
          dispatch(walletApi.util.invalidateTags(["utilityWallet"]));
        } catch (error: any) {
          const message = error?.error?.data;

          if (typeof message === "string") customToast.error(message);
          else customToast.error("Unable to fund, try again later");
        }
      },
    }),
    fundWallet: builders.mutation<
      APIResponseModel<IappWallet>,
      WalletTransactionPayloadType
    >({
      query: (data) => {
        const { method, url: makeUrl } = ENDPOINTS.wallet.fund_wallet;
        const url = makeUrl(data.appid);
        return {
          url,
          method,
          data: data.data,
        };
      },
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          await queryFulfilled;
        } catch (error: any) {
          const message = error?.error?.data;

          if (typeof message === "string") customToast.error(message);
          else customToast.error("Unable to fund, try again later");
        }
      },
    }),
    fundWalletPooling: builders.mutation<
      APIResponseModel<IFundPolling>,
      { appid: string; wallet_type?: IWalletType }
    >({
      query: (data) => {
        const { method, url: makeUrl } = ENDPOINTS.wallet.fund_wallet_resolve;
        const url = makeUrl(data.appid);
        return {
          url,
          method,
        };
      },
      onQueryStarted: async (data, { queryFulfilled, dispatch }) => {
        try {
          const response = (await queryFulfilled).data;

          if (response.data.payment_status === "paid") {
            switch (data.wallet_type) {
              case "commission":
                dispatch(walletApi.util.invalidateTags(["commisionWallet"]));
                break;
              case "kyc":
                dispatch(walletApi.util.invalidateTags(["kycWallet"]));
                break;
              default:
                dispatch(walletApi.util.invalidateTags(["treasuryWallet"]));
            }
          }
        } catch (error: any) {
          const message = error?.error?.data;

          if (typeof message === "string") customToast.error(message);
          else customToast.error("Unable to fund, try again later");
        }
      },
    }),
    bankWithdrawal: builders.mutation<
      APIResponseModel<IbankWithdrawalResponse>,
      { appid: string; data: IbankWithdrawal }
    >({
      query: (data) => {
        const { method, url: makeUrl } = ENDPOINTS.withdrawal.bank_withdrawal;
        const url = makeUrl(data.appid);
        return {
          url,
          method,
          data: data.data,
        };
      },
      onQueryStarted: async (data, { queryFulfilled, dispatch }) => {
        try {
          await queryFulfilled;
          switch (data.data.wallet_type) {
            case "commission":
              dispatch(walletApi.util.invalidateTags(["commisionWallet"]));
              break;
            case "kyc":
              dispatch(walletApi.util.invalidateTags(["kycWallet"]));
              break;
            default:
              dispatch(walletApi.util.invalidateTags(["treasuryWallet"]));
          }
        } catch (error: any) {
          const message = error?.error?.data;

          if (typeof message === "string") customToast.error(message);
          else customToast.error("Unable to withdraw, try again later");
        }
      },
    }),
  }),
});

export const {
  useGetCommisionWalletQuery,
  useGetKycWalletQuery,
  useGetTreasuryWalletQuery,
  useGetKycTransactionsQuery,
  useGetCommisionTransactionsQuery,
  useGetTreasuryTransactionsQuery,
  useGetMerchantCardWalletQuery,
  useGetUtilitiesWalletQuery,
  useGetCardTransactionsQuery,
  useGetUtilitiesTransactionsQuery,
  useGetCommisionLedgerBalanceQuery,
  useFundUtilityWalletMutation,
  useWithdrawUtilityWalletMutation,
  useFundWalletMutation,
  useFundWalletPoolingMutation,
  useBankWithdrawalMutation,
} = walletApi;
